import { computed } from "@vue/composition-api"
import { WorkspaceModule } from "@/store"
import { Dictionary, PluginExceptionAny } from "@/types/core"
import { Service } from "@/types/workspace"

export const useServices = () => {
  const workspaceModule = WorkspaceModule()

  const isDraft = computed(() => {
    return workspaceModule.watching.schemaDeployed === null
  })

  /**
   * The services that are currently in the schema
   * and the services that are deployed
   */
  const services = computed(() => {
    return {
      current: workspaceModule.watching.schema.services ?? [],
      deployed: workspaceModule.watching.schemaDeployed?.services ?? []
    }
  })

  /**
   * The services that aren't included in any other criteria
   * (new, modified, removed)
   */
  const pureServices = computed<Service[]>(() => {
    if (!isDraft.value) {
      const mergedServices = [...services.value.deployed, ...services.value.current]

      const uniqueServices = mergedServices.filter((service, index) => {
        return mergedServices.findIndex(s => s.id === service.id) === index
      })

      return uniqueServices.reduce((collection: Service[], service) => {
        const notNew = newServices.value.every(newService => newService.id !== service.id)
        const notModified = modified.value.every(modifiedService => modifiedService.id !== service.id)
        const notRemoved = removed.value.every(removedService => removedService.id !== service.id)

        if (notNew && notModified && notRemoved) {
          collection.push(service)
        }

        return collection
      }, [])
    } else {
      return services.value.current
    }
  })

  /**
   * Services added to schema and not deployed schema
   */
  const newServices = computed<Service[]>(() => {
    if (isDraft.value) return []

    return services.value.current.filter(currentService => {
      return !services.value.deployed.some(deployedService => {
        return deployedService.id === currentService.id
      })
    })
  })

  /**
   * Services that are deployed and have been modified
   * (i.e. the deployed service is different from the current service)
   */
  const modified = computed<Service[]>(() => {
    if (isDraft.value) return []

    return services.value.deployed.filter(deployedService => {
      return services.value.current.some(currentService => {
        const matchingService = deployedService.id === currentService.id
        return matchingService && !_deepEqual(deployedService, currentService)
      })
    })
  })

  /**
   * Services that are deployed and not in the current schema
   * (i.e. the deployed service is not in the current service)
   */
  const removed = computed<Service[]>(() => {
    if (isDraft.value) return []

    return services.value.deployed.filter(deployedService => {
      return !services.value.current.some(currentService => {
        return deployedService.id === currentService.id
      })
    })
  })

  const _deepEqual = (objectOne: Dictionary<PluginExceptionAny>, objectTwo: Dictionary<PluginExceptionAny>) => {
    if (objectOne === objectTwo) return true

    if (typeof objectOne !== "object" || objectOne === null || typeof objectTwo !== "object" || objectTwo === null) {
      return false
    }

    const keysOne = Object.keys(objectOne)
    const keysTwo = Object.keys(objectTwo)
    if (keysOne.length !== keysTwo.length) return false

    for (const key in keysOne) {
      if (!_deepEqual(objectOne[key], objectTwo[key])) {
        return false
      }
    }

    return true
  }

  return {
    pureServices,
    newServices,
    removed,
    modified,
    services
  }
}
